import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TrendingDown, TrendingUp, Minus, Thermometer, AlertTriangle, ArrowRight, Users } from "lucide-react";
import { toast } from "sonner";
import { getSupplierTrend, fmt } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { Sparkline } from "@/components/Sparkline";

const MONTH_SHORT = { en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  ro: ["Ian", "Feb", "Mar", "Apr", "Mai", "Iun", "Iul", "Aug", "Sep", "Oct", "Noi", "Dec"] };
const monthLabel = (key, lang) => { const [y, m] = key.split("-").map(Number); return `${(MONTH_SHORT[lang] || MONTH_SHORT.en)[m - 1]} ${String(y).slice(2)}`; };

const Delta = ({ value, unit, inverse = false, decimals = 2, testId }) => {
  if (value == null) return <span className="text-xs text-muted-foreground">—</span>;
  const good = inverse ? value < 0 : value > 0;
  const flat = Math.abs(value) < (decimals === 1 ? 0.05 : 0.005);
  const cls = flat ? "text-muted-foreground" : good ? "text-[hsl(var(--success))]" : "text-[hsl(var(--danger))]";
  const Icon = flat ? Minus : value > 0 ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 font-mono text-[11px] tabular-nums ${cls}`} data-testid={testId}>
      <Icon className="h-3 w-3" /> {value > 0 ? "+" : ""}{fmt(value, decimals)}{unit}
    </span>
  );
};

/** Metric cell: latest value, delta vs. earlier months and a sparkline with per-month tooltip. */
const MetricCell = ({ series, months, latest, delta, unit, decimals = 2, inverse = false, lang, testId, overLimit = false }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <div className="inline-flex cursor-default flex-col gap-0.5" data-testid={testId}>
        <div className="flex items-center gap-2">
          <span className={`font-mono text-sm font-semibold tabular-nums ${overLimit ? "text-[hsl(var(--danger))]" : ""}`}>
            {latest == null ? "—" : `${fmt(latest, decimals)}${unit}`}
          </span>
          <Delta value={delta} unit={unit} inverse={inverse} decimals={decimals} />
        </div>
        <Sparkline values={series} width={84} height={22} tone={inverse ? "inverse" : "auto"} />
      </div>
    </TooltipTrigger>
    <TooltipContent className="p-3">
      <div className="grid grid-cols-[auto_auto] gap-x-4 gap-y-0.5 text-xs">
        {months.map((mo, i) => (
          <React.Fragment key={mo}>
            <span className="opacity-75">{monthLabel(mo, lang)}</span>
            <span className="text-right font-mono tabular-nums">{series[i] == null ? "—" : `${fmt(series[i], decimals)}${unit}`}</span>
          </React.Fragment>
        ))}
      </div>
    </TooltipContent>
  </Tooltip>
);

/**
 * Supplier quality trend — monthly quantity-weighted fat %, protein %, temperature and volume per milk supplier.
 * `compact` renders the dashboard card (top suppliers, flagged first, link to the full view).
 */
export const SupplierTrend = ({ compact = false, limit = 5 }) => {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [months, setMonths] = useState("6");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getSupplierTrend(Number(months))
      .then(setData)
      .catch(() => { if (!compact) toast.error(t("supplierTrend.loadError")); })
      .finally(() => setLoading(false));
  }, [months]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = (data?.suppliers || []).slice(0, compact ? limit : undefined);
  const mk = data?.months || [];
  const flagLabel = (f) => t(`supplierTrend.flag_${f}`);
  const flaggedCount = (data?.suppliers || []).filter((s) => s.flags.length).length;

  const table = (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("supplierTrend.colSupplier")}</TableHead>
            <TableHead>{t("supplierTrend.colVolume")}</TableHead>
            <TableHead>{t("milk.fatPct")}</TableHead>
            <TableHead>{t("milk.proteinPct")}</TableHead>
            <TableHead><span className="inline-flex items-center gap-1"><Thermometer className="h-3.5 w-3.5" /> {t("milk.tempC")}</span></TableHead>
            {!compact && <TableHead>{t("supplierTrend.colSignals")}</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <TableRow key={i}>{Array.from({ length: compact ? 5 : 6 }).map((__, j) => <TableCell key={j}><Skeleton className="h-6 w-full" /></TableCell>)}</TableRow>
            ))
          ) : rows.length === 0 ? (
            <TableRow><TableCell colSpan={compact ? 5 : 6} className="py-8 text-center text-sm text-muted-foreground">{t("supplierTrend.empty")}</TableCell></TableRow>
          ) : rows.map((s) => (
            <TableRow key={s.supplier} data-testid={`supplier-trend-row-${s.supplier}`}>
              <TableCell>
                <div className="flex items-center gap-2 font-medium">
                  {s.flags.length > 0 && <AlertTriangle className="h-4 w-4 shrink-0 text-[hsl(var(--warning))]" />}
                  <span className="truncate">{s.supplier}</span>
                </div>
                <div className="text-[11px] text-muted-foreground">{t("supplierTrend.receptions", { n: s.receptions })}{s.over_temp_count ? ` · ${t("supplierTrend.overTemp", { n: s.over_temp_count })}` : ""}</div>
                {compact && s.flags.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">{s.flags.map((f) => <Badge key={f} className="badge-warn border-transparent text-[10px]">{flagLabel(f)}</Badge>)}</div>
                )}
              </TableCell>
              <TableCell>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="inline-flex cursor-default flex-col gap-0.5">
                      <span className="font-mono text-sm tabular-nums">{fmt(s.total_l, 0)} L</span>
                      <Sparkline values={s.series.qty} width={84} height={22} tone="neutral" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="p-3">
                    <div className="grid grid-cols-[auto_auto] gap-x-4 gap-y-0.5 text-xs">
                      {mk.map((mo, i) => (<React.Fragment key={mo}><span className="opacity-75">{monthLabel(mo, lang)}</span><span className="text-right font-mono tabular-nums">{fmt(s.series.qty[i], 0)} L</span></React.Fragment>))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TableCell>
              <TableCell><MetricCell series={s.series.fat} months={mk} latest={s.latest.fat} delta={s.delta.fat} unit="%" lang={lang} testId={`supplier-fat-${s.supplier}`} /></TableCell>
              <TableCell><MetricCell series={s.series.protein} months={mk} latest={s.latest.protein} delta={s.delta.protein} unit="%" lang={lang} testId={`supplier-protein-${s.supplier}`} /></TableCell>
              <TableCell><MetricCell series={s.series.temperature} months={mk} latest={s.latest.temperature} delta={s.delta.temperature} unit="°" decimals={1} inverse lang={lang} testId={`supplier-temp-${s.supplier}`} overLimit={s.latest.temperature != null && data && s.latest.temperature > data.max_temp} /></TableCell>
              {!compact && (
                <TableCell>
                  {s.flags.length === 0 ? <Badge className="badge-ok border-transparent text-[10px]">{t("supplierTrend.stable")}</Badge>
                    : <div className="flex flex-wrap gap-1">{s.flags.map((f) => <Badge key={f} className={`${f === "temp_over" ? "badge-bad" : "badge-warn"} border-transparent text-[10px]`}>{flagLabel(f)}</Badge>)}</div>}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <TooltipProvider delayDuration={150}>
      <Card data-testid={compact ? "dash-supplier-trend" : "supplier-trend"}>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base"><Users className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("supplierTrend.title")}</CardTitle>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("supplierTrend.subtitle", { n: mk.length || months })}{!loading && flaggedCount > 0 ? ` · ${t("supplierTrend.flagged", { n: flaggedCount })}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Tabs value={months} onValueChange={setMonths}>
              <TabsList className="h-8">
                <TabsTrigger value="6" className="h-6 px-2.5 text-xs" data-testid="supplier-trend-6m">{t("supplierTrend.months", { n: 6 })}</TabsTrigger>
                <TabsTrigger value="12" className="h-6 px-2.5 text-xs" data-testid="supplier-trend-12m">{t("supplierTrend.months", { n: 12 })}</TabsTrigger>
              </TabsList>
            </Tabs>
            {compact && (
              <Button variant="ghost" size="sm" className="h-8" onClick={() => navigate("/app/milk?tab=suppliers")} data-testid="supplier-trend-open">
                {t("supplierTrend.viewAll")} <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className={compact ? "pt-0" : ""}>
          {table}
          {!compact && <p className="mt-3 text-[11px] text-muted-foreground">{t("supplierTrend.footnote")}</p>}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};
